import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import InputWrapper from "../InputWrapper";
import useDropDownPositioning from "../../../utils/inputUtils/useDropDownPositioning";
import {
  isDataValid,
  isValueValid,
  prepareData,
  prepareRowClickUpdate,
  filterContent,
  checkData
} from "../../../utils/inputUtils/autoCompleteUtils";
import style from "../../../assets/css/styles.module.scss";
import useDeprecated from "../../../hooks/useDeprecated";

const DepDropDown = ({
  data,
  onChange,
  value,
  placeholder,
  id,
  errorMsg,
  className,
  options,
  isValid,
  name,
  label,
  afterInputText,
  description,
  customFilter,
  searchBy,
  descriptionLength,
  parentId,
  dropDownId,
  showCodeFromRigth
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropDownCustomStyle, setDropDownCustomStyle] = useState({});

  useDeprecated("DepDropDown", "DropDown");

  const { code } = value;

  const updateElement = useCallback(event => onChange(prepareData(event, data, searchBy)), [data, onChange, searchBy]);

  const rowClicked = useCallback(newValue => onChange(prepareRowClickUpdate(newValue, value, data)), [
    data,
    onChange,
    value
  ]);

  const checkValue = useCallback(
    event => {
      if (!checkData(event, searchBy, data)) {
        updateElement({ target: { value: "" } });
      } else {
        updateElement(event);
      }
      setShowDropdown(false);
      if (options.onBlur) {
        options.onBlur(name);
      }
    },
    [data, name, options, searchBy, updateElement]
  );

  useDropDownPositioning(showDropdown, parentId, setDropDownCustomStyle, dropDownId, value, id);

  return useMemo(
    () => (
      <InputWrapper
        className={className}
        isValid={isValid}
        errorMsg={errorMsg}
        label={label}
        afterInputText={afterInputText}
      >
        <div className={`${style.autoComplete} ${style.dropDown}`}>
          <div className={style.dropdown}>
            <input
              id={id}
              name={name}
              type="text"
              onChange={updateElement}
              onFocus={() => setShowDropdown(true)}
              value={value[searchBy]}
              autoComplete="off"
              placeholder={placeholder}
              {...options}
              onBlur={checkValue}
              className={isValid === false ? `${style.inputError} ${style.dropDownMainInput}` : style.dropDownMainInput}
            />
            <input
              type="text"
              className={isValid === false ? `${style.inputError} ${style.inputRightText}` : style.inputRightText}
              value={showCodeFromRigth ? code : ""}
              disabled
            />
            {value[description] && (
              <span className={style.showName} id={`desc${dropDownId}`}>
                {`${value[description].slice(0, descriptionLength)} ${
                  value[description].length > descriptionLength ? "..." : ""
                }`}
              </span>
            )}
            <div
              id={`input${dropDownId}`}
              className={`${style.dropdownContent} defaultDropDown ${showDropdown ? "" : style.hidden}`}
              style={dropDownCustomStyle}
            >
              {filterContent(value[searchBy], customFilter, data, searchBy).map(item => (
                <button
                  type="button"
                  key={item.code}
                  className={style.dropdownRow}
                  onMouseDown={() => rowClicked(item)}
                >
                  <div key="code">{item.code}</div>
                  {Object.keys(item).map(i =>
                    i !== "code" ? (
                      <div key={i} className={style.description}>
                        {item[i]}
                      </div>
                    ) : (
                      ""
                    )
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </InputWrapper>
    ),
    [
      afterInputText,
      checkValue,
      className,
      code,
      customFilter,
      data,
      description,
      descriptionLength,
      dropDownCustomStyle,
      dropDownId,
      errorMsg,
      id,
      isValid,
      label,
      name,
      options,
      placeholder,
      rowClicked,
      searchBy,
      showCodeFromRigth,
      showDropdown,
      updateElement,
      value
    ]
  );
};

DepDropDown.displayName = "DropDown";

DepDropDown.defaultProps = {
  id: "",
  name: "",
  data: [{ code: "" }],
  value: { code: "" },
  placeholder: "",
  onChange: () => {},
  errorMsg: null,
  className: null,
  options: {},
  isValid: null,
  label: null,
  afterInputText: null,
  description: "",
  descriptionLength: Infinity,
  customFilter: null,
  searchBy: "code",
  parentId: "",
  showCodeFromRigth: false,
  dropDownId: ""
};

DepDropDown.propTypes = {
  /** DropDown id */
  id: PropTypes.string,
  /** DropDown id. Needed for calculating dropdown position if content is small */
  dropDownId: PropTypes.string,
  /** DropDown name */
  name: PropTypes.string,
  /** DropDown data for searching. "code" property is required */
  data: isDataValid,
  /** prop for showing description next to input */
  description: PropTypes.string,
  /** description text length */
  descriptionLength: PropTypes.number,
  /** Parent id for calculating dropdown position */
  parentId: PropTypes.string,
  /** Field name which will be used for input and search */
  searchBy: PropTypes.string,
  /** Custom filter function for DropDown */
  customFilter: PropTypes.func,
  /** DropDown input value. "code" property is required */
  value: isValueValid,
  /** Set to true if you want to show the code from the right part */
  showCodeFromRigth: PropTypes.bool,
  /** DropDown placeHolder */
  placeholder: PropTypes.string,
  /** Add any html attributes to the component */
  options: PropTypes.object,
  /** Change event callback */
  onChange: PropTypes.func,
  /** DropDown validation error message */
  errorMsg: PropTypes.string,
  /** Add className to the component */
  className: PropTypes.string,
  /** Is DropDown valid */
  isValid: PropTypes.bool,
  /** If provided, will add label before the input */
  label: PropTypes.string,
  /** If provided, will add text after the input */
  afterInputText: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default DepDropDown;
